import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Autocomplete,
    Avatar,
    Box,
    Button,
    Card,
    CardContent,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    IconButton,
    InputAdornment,
    Link,
    Popover,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import {
    Add as AddIcon,
    ContentCopy as ContentCopyIcon,
    Delete as DeleteIcon,
    Edit as EditIcon,
    Key as KeyIcon,
    Language as LanguageIcon,
    OpenInNew as OpenInNewIcon,
    PhotoCamera as PhotoCameraIcon,
    Visibility as VisibilityIcon,
    VisibilityOff as VisibilityOffIcon,
} from '@mui/icons-material';
import { TokenContext } from '../Context/TokenContext';
import { UserContext } from '../Context/UserContext';
import {
    getDemoSites,
    createDemoSite,
    updateDemoSite,
    deleteDemoSite,
    uploadDemoSiteImage,
    deleteDemoSiteImage,
    createDemoSiteAccess,
    updateDemoSiteAccess,
    deleteDemoSiteAccess,
} from '../Utils/demoSitesApi';

const API_URL = process.env.REACT_APP_BACKEND_API_URL || 'http://localhost:8080';

const emptyForm = { name: '', url: '', category: '', subcategory: '' };
const emptyAccessForm = { label: '', url: '', login: '', password: '' };
const UNCATEGORIZED = 'Sans catégorie';

// ── Popover "Accès rapide" sur une carte : ouvrir / copier sans passer par l'édition ──

function AccessQuickView({ site, canReveal }) {
    const [anchorEl, setAnchorEl] = useState(null);
    const [revealed, setRevealed] = useState({});
    const accesses = site.Accesses || [];

    if (accesses.length === 0) return null;

    const copy = (value) => {
        if (value) navigator.clipboard?.writeText(value).catch(() => {});
    };

    return (
        <>
            <Tooltip title="Accès">
                <IconButton size="small" onClick={(e) => setAnchorEl(e.currentTarget)}>
                    <KeyIcon fontSize="small" />
                </IconButton>
            </Tooltip>
            <Popover
                open={!!anchorEl}
                anchorEl={anchorEl}
                onClose={() => setAnchorEl(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <Stack spacing={1.5} sx={{ p: 2, minWidth: 280, maxWidth: 340 }}>
                    {accesses.map((access, idx) => (
                        <React.Fragment key={access.id}>
                            {idx > 0 && <Divider />}
                            <Box>
                                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>{access.label}</Typography>
                                {access.url && (
                                    <Link href={access.url} target="_blank" rel="noopener noreferrer"
                                          sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontSize: '0.8rem' }}>
                                        {access.url}
                                        <OpenInNewIcon sx={{ fontSize: 13 }} />
                                    </Link>
                                )}
                                {access.login && (
                                    <Stack direction="row" spacing={0.5} alignItems="center">
                                        <Typography variant="caption" color="text.secondary">Identifiant : {access.login}</Typography>
                                        <IconButton size="small" onClick={() => copy(access.login)}>
                                            <ContentCopyIcon sx={{ fontSize: 13 }} />
                                        </IconButton>
                                    </Stack>
                                )}
                                {(access.password || access.has_password) && (
                                    <Stack direction="row" spacing={0.5} alignItems="center">
                                        <Typography variant="caption" color="text.secondary">
                                            Mot de passe : {canReveal && revealed[access.id] ? access.password : '••••••••'}
                                        </Typography>
                                        {canReveal && access.password && (
                                            <>
                                                <IconButton size="small" onClick={() => setRevealed((p) => ({ ...p, [access.id]: !p[access.id] }))}>
                                                    {revealed[access.id] ? <VisibilityOffIcon sx={{ fontSize: 13 }} /> : <VisibilityIcon sx={{ fontSize: 13 }} />}
                                                </IconButton>
                                                <IconButton size="small" onClick={() => copy(access.password)}>
                                                    <ContentCopyIcon sx={{ fontSize: 13 }} />
                                                </IconButton>
                                            </>
                                        )}
                                    </Stack>
                                )}
                            </Box>
                        </React.Fragment>
                    ))}
                </Stack>
            </Popover>
        </>
    );
}

// ── Ligne d'accès éditable (dans le dialog de la fiche) ─────────────────────

function AccessRow({ access, accessToken, onSaved, onDeleted }) {
    const [form, setForm] = useState({
        label: access.label || '',
        url: access.url || '',
        login: access.login || '',
        password: access.password || '',
    });
    const [showPassword, setShowPassword] = useState(false);
    const [saving, setSaving] = useState(false);
    // Sans demo-sites.credentials, le serveur ne renvoie pas le mot de passe (has_password à la
    // place) : on ne le renvoie que s'il a été retapé, sinon on écraserait l'existant par ''.
    const passwordHidden = access.password === undefined && access.has_password === true;
    const [passwordTouched, setPasswordTouched] = useState(false);

    const save = () => {
        setSaving(true);
        const { password, ...rest } = form;
        const payload = passwordHidden && !passwordTouched ? rest : form;
        updateDemoSiteAccess(accessToken, access.id, payload)
            .then(onSaved)
            .finally(() => setSaving(false));
    };

    return (
        <Stack spacing={1} sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
            <Stack direction="row" spacing={1}>
                <TextField
                    label="Libellé" size="small" fullWidth value={form.label}
                    placeholder="Accès administrateur"
                    onChange={(e) => setForm((p) => ({ ...p, label: e.target.value }))}
                    onBlur={save}
                />
                <IconButton size="small" color="error" onClick={() => onDeleted(access.id)} disabled={saving}>
                    <DeleteIcon fontSize="small" />
                </IconButton>
            </Stack>
            <TextField
                label="URL" size="small" fullWidth value={form.url}
                onChange={(e) => setForm((p) => ({ ...p, url: e.target.value }))}
                onBlur={save}
            />
            <Stack direction="row" spacing={1}>
                <TextField
                    label="Identifiant" size="small" fullWidth value={form.login}
                    onChange={(e) => setForm((p) => ({ ...p, login: e.target.value }))}
                    onBlur={save}
                />
                <TextField
                    label="Mot de passe" size="small" fullWidth value={form.password}
                    type={showPassword ? 'text' : 'password'}
                    placeholder={passwordHidden ? '•••••••• (inchangé)' : undefined}
                    onChange={(e) => { setPasswordTouched(true); setForm((p) => ({ ...p, password: e.target.value })); }}
                    onBlur={save}
                    slotProps={{
                        inputLabel: { shrink: passwordHidden || undefined },
                        input: {
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton size="small" onClick={() => setShowPassword((v) => !v)}>
                                        {showPassword ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        },
                    }}
                />
            </Stack>
        </Stack>
    );
}

export default function DemoSitesPage() {
    const { accessToken } = useContext(TokenContext);
    const { userInfo } = useContext(UserContext);
    const isSuperAdmin = userInfo?.is_super_admin === true;
    const userPermissions = userInfo?.permissions || [];
    const canManageSites = isSuperAdmin || userPermissions.includes('demo-sites.manage');
    const canRevealCredentials = isSuperAdmin || userPermissions.includes('demo-sites.credentials');

    const [sites, setSites] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingSite, setEditingSite] = useState(null);
    const [form, setForm] = useState(emptyForm);
    const [formError, setFormError] = useState('');
    const [saving, setSaving] = useState(false);

    const [uploadingImage, setUploadingImage] = useState(false);
    const [imageError, setImageError] = useState(null);

    const [newAccessForm, setNewAccessForm] = useState(emptyAccessForm);
    const [addingAccess, setAddingAccess] = useState(false);

    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleting, setDeleting] = useState(false);

    const fetchSites = useCallback(() => {
        if (!accessToken) return;
        setLoading(true);
        getDemoSites(accessToken)
            .then(setSites)
            .catch(() => setError('Impossible de charger les sites de démo'))
            .finally(() => setLoading(false));
    }, [accessToken]);

    useEffect(() => { fetchSites(); }, [fetchSites]);

    const categoryOptions = useMemo(
        () => Array.from(new Set(sites.map((s) => s.category).filter(Boolean))).sort(),
        [sites]
    );
    const subcategoryOptions = useMemo(
        () => Array.from(new Set(
            sites
                .filter((s) => !form.category || s.category === form.category)
                .map((s) => s.subcategory)
                .filter(Boolean)
        )).sort(),
        [sites, form.category]
    );

    // Groupement catégorie -> sous-catégorie -> sites
    const grouped = useMemo(() => {
        const byCategory = new Map();
        for (const site of sites) {
            const cat = site.category || UNCATEGORIZED;
            const sub = site.subcategory || '';
            if (!byCategory.has(cat)) byCategory.set(cat, new Map());
            const bySub = byCategory.get(cat);
            if (!bySub.has(sub)) bySub.set(sub, []);
            bySub.get(sub).push(site);
        }
        return byCategory;
    }, [sites]);

    // ── Dialog création / édition ───────────────────────────────────────

    const openCreate = () => {
        setEditingSite(null);
        setForm(emptyForm);
        setFormError('');
        setImageError(null);
        setNewAccessForm(emptyAccessForm);
        setDialogOpen(true);
    };

    const openEdit = (site) => {
        setEditingSite(site);
        setForm({
            name: site.name || '',
            url: site.url || '',
            category: site.category || '',
            subcategory: site.subcategory || '',
        });
        setFormError('');
        setImageError(null);
        setNewAccessForm(emptyAccessForm);
        setDialogOpen(true);
    };

    const closeDialog = () => {
        if (saving || uploadingImage) return;
        setDialogOpen(false);
        fetchSites();
    };

    const handleSave = () => {
        if (!form.name.trim() || !form.url.trim()) {
            setFormError('Le nom et l\'URL sont requis');
            return;
        }

        setSaving(true);
        setFormError('');

        const payload = {
            name: form.name.trim(),
            url: form.url.trim(),
            category: form.category.trim(),
            subcategory: form.subcategory.trim(),
        };

        const request = editingSite
            ? updateDemoSite(accessToken, editingSite.id, payload)
            : createDemoSite(accessToken, payload);

        request
            .then((saved) => {
                setEditingSite({ ...saved, Accesses: editingSite?.Accesses || [] });
                fetchSites();
                if (!editingSite) {
                    setForm({
                        name: saved.name || '',
                        url: saved.url || '',
                        category: saved.category || '',
                        subcategory: saved.subcategory || '',
                    });
                } else {
                    setDialogOpen(false);
                }
            })
            .catch((err) => setFormError(err.response?.data?.message || 'Erreur lors de l\'enregistrement'))
            .finally(() => setSaving(false));
    };

    const handleImageChange = (event) => {
        const file = event.target.files?.[0];
        event.target.value = '';
        if (!file || !editingSite) return;

        setUploadingImage(true);
        setImageError(null);

        uploadDemoSiteImage(accessToken, editingSite.id, file)
            .then((updated) => {
                setEditingSite((prev) => ({ ...prev, ...updated }));
                setSites((prev) => prev.map((s) => (s.id === updated.id ? { ...s, ...updated } : s)));
            })
            .catch((err) => setImageError(err.response?.data?.message || 'Erreur lors de l\'envoi de l\'image'))
            .finally(() => setUploadingImage(false));
    };

    const handleImageDelete = () => {
        if (!editingSite) return;

        setUploadingImage(true);
        setImageError(null);

        deleteDemoSiteImage(accessToken, editingSite.id)
            .then((updated) => {
                setEditingSite((prev) => ({ ...prev, ...updated }));
                setSites((prev) => prev.map((s) => (s.id === updated.id ? { ...s, ...updated } : s)));
            })
            .catch((err) => setImageError(err.response?.data?.message || 'Erreur lors de la suppression de l\'image'))
            .finally(() => setUploadingImage(false));
    };

    // ── Accès ────────────────────────────────────────────────────────────

    const handleAddAccess = () => {
        if (!editingSite || !newAccessForm.label.trim()) return;

        setAddingAccess(true);
        createDemoSiteAccess(accessToken, { ...newAccessForm, demo_site_id: editingSite.id })
            .then((created) => {
                setEditingSite((prev) => ({ ...prev, Accesses: [...(prev.Accesses || []), created] }));
                setNewAccessForm(emptyAccessForm);
            })
            .finally(() => setAddingAccess(false));
    };

    const handleAccessSaved = (updated) => {
        setEditingSite((prev) => ({
            ...prev,
            Accesses: (prev.Accesses || []).map((a) => (a.id === updated.id ? updated : a)),
        }));
    };

    const handleAccessDeleted = (accessId) => {
        deleteDemoSiteAccess(accessToken, accessId).then(() => {
            setEditingSite((prev) => ({
                ...prev,
                Accesses: (prev.Accesses || []).filter((a) => a.id !== accessId),
            }));
        });
    };

    // ── Suppression du site ─────────────────────────────────────────────

    const confirmDelete = () => {
        if (!deleteTarget) return;
        setDeleting(true);
        deleteDemoSite(accessToken, deleteTarget.id)
            .then(() => {
                setDeleteTarget(null);
                fetchSites();
            })
            .catch(() => setError('Impossible de supprimer ce site'))
            .finally(() => setDeleting(false));
    };

    return (
        <Box sx={{ maxWidth: '1400px', mx: 'auto', p: { xs: 2, sm: 3 } }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }} spacing={2} sx={{ mb: 3 }}>
                <Box>
                    <Typography variant="h4" sx={{ fontWeight: 600 }}>Sites de démo</Typography>
                    <Typography variant="body2" color="text.secondary">
                        URLs, catégories, images et accès (identifiants) des sites de démonstration
                    </Typography>
                </Box>
                {canManageSites && (
                    <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
                        Ajouter un site
                    </Button>
                )}
            </Stack>

            {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                    <CircularProgress />
                </Box>
            ) : sites.length === 0 ? (
                <Typography color="text.secondary">Aucun site de démo pour le moment.</Typography>
            ) : (
                Array.from(grouped.entries()).map(([category, bySub]) => (
                    <Box key={category} sx={{ mb: 4 }}>
                        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>{category}</Typography>
                        {Array.from(bySub.entries()).map(([subcategory, items]) => (
                            <Box key={subcategory || '_'} sx={{ mb: 2 }}>
                                {subcategory && (
                                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                                        {subcategory}
                                    </Typography>
                                )}
                                <Box sx={{
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
                                    gap: 2,
                                }}>
                                    {items.map((site) => (
                                        <Card key={site.id} sx={{ border: '1px solid', borderColor: 'divider', boxShadow: 'none' }}>
                                            <Box sx={{
                                                height: 120,
                                                bgcolor: 'action.hover',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                overflow: 'hidden',
                                            }}>
                                                {site.image ? (
                                                    <Box
                                                        component="img"
                                                        src={`${API_URL}${site.image}`}
                                                        alt={site.name}
                                                        sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                                    />
                                                ) : (
                                                    <LanguageIcon sx={{ fontSize: 40, color: 'text.disabled' }} />
                                                )}
                                            </Box>
                                            <CardContent sx={{ p: 2 }}>
                                                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }} noWrap>
                                                    {site.name}
                                                </Typography>
                                                <Link
                                                    href={site.url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontSize: '0.8rem', mb: 1 }}
                                                    noWrap
                                                >
                                                    {site.url}
                                                    <OpenInNewIcon sx={{ fontSize: 14 }} />
                                                </Link>
                                                <Stack direction="row" spacing={0.5} justifyContent="flex-end" alignItems="center">
                                                    <AccessQuickView site={site} canReveal={canRevealCredentials} />
                                                    {canManageSites && (
                                                        <>
                                                            <IconButton size="small" onClick={() => openEdit(site)}>
                                                                <EditIcon fontSize="small" />
                                                            </IconButton>
                                                            <IconButton size="small" color="error" onClick={() => setDeleteTarget(site)}>
                                                                <DeleteIcon fontSize="small" />
                                                            </IconButton>
                                                        </>
                                                    )}
                                                </Stack>
                                            </CardContent>
                                        </Card>
                                    ))}
                                </Box>
                            </Box>
                        ))}
                    </Box>
                ))
            )}

            {/* Dialog création / édition */}
            <Dialog open={dialogOpen} onClose={closeDialog} fullWidth maxWidth="sm">
                <DialogTitle>{editingSite ? 'Modifier le site de démo' : 'Ajouter un site de démo'}</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        {formError && <Alert severity="error">{formError}</Alert>}

                        {editingSite && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Avatar
                                    variant="rounded"
                                    src={editingSite.image ? `${API_URL}${editingSite.image}` : undefined}
                                    sx={{ width: 64, height: 64, bgcolor: 'action.hover' }}
                                >
                                    {!editingSite.image && <PhotoCameraIcon color="disabled" />}
                                </Avatar>
                                <Stack direction="row" spacing={1}>
                                    <Button
                                        component="label"
                                        size="small"
                                        variant="outlined"
                                        startIcon={uploadingImage ? <CircularProgress size={16} /> : <PhotoCameraIcon />}
                                        disabled={uploadingImage}
                                    >
                                        {editingSite.image ? "Changer l'image" : 'Ajouter une image'}
                                        <input
                                            type="file"
                                            accept="image/jpeg,image/png,image/gif,image/webp"
                                            hidden
                                            onChange={handleImageChange}
                                        />
                                    </Button>
                                    {editingSite.image && (
                                        <Button
                                            size="small"
                                            color="error"
                                            variant="outlined"
                                            disabled={uploadingImage}
                                            onClick={handleImageDelete}
                                        >
                                            Supprimer
                                        </Button>
                                    )}
                                </Stack>
                            </Box>
                        )}
                        {!editingSite && (
                            <Alert severity="info">
                                Enregistre d'abord le site pour pouvoir y ajouter une image et des accès.
                            </Alert>
                        )}
                        {imageError && <Alert severity="error">{imageError}</Alert>}

                        <TextField
                            label="Nom"
                            value={form.name}
                            onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
                            fullWidth
                            size="small"
                        />
                        <TextField
                            label="URL"
                            value={form.url}
                            onChange={(e) => setForm((prev) => ({ ...prev, url: e.target.value }))}
                            fullWidth
                            size="small"
                            placeholder="https://bijoux.demo.alais-studio.fr"
                        />
                        <Autocomplete
                            freeSolo
                            options={categoryOptions}
                            value={form.category}
                            onInputChange={(_e, value) => setForm((prev) => ({ ...prev, category: value }))}
                            renderInput={(params) => <TextField {...params} label="Catégorie" size="small" />}
                        />
                        <Autocomplete
                            freeSolo
                            options={subcategoryOptions}
                            value={form.subcategory}
                            onInputChange={(_e, value) => setForm((prev) => ({ ...prev, subcategory: value }))}
                            renderInput={(params) => <TextField {...params} label="Sous-catégorie" size="small" />}
                        />

                        {editingSite && (
                            <>
                                <Divider sx={{ mt: 1 }} />
                                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                    Accès (facultatifs — plusieurs possibles : administrateur, utilisateur...)
                                </Typography>

                                {(editingSite.Accesses || []).map((access) => (
                                    <AccessRow
                                        key={access.id}
                                        access={access}
                                        accessToken={accessToken}
                                        onSaved={handleAccessSaved}
                                        onDeleted={handleAccessDeleted}
                                    />
                                ))}

                                <Stack spacing={1} sx={{ p: 1.5, border: '1px dashed', borderColor: 'divider', borderRadius: 1 }}>
                                    <TextField
                                        label="Libellé du nouvel accès" size="small" fullWidth
                                        placeholder="Accès administrateur"
                                        value={newAccessForm.label}
                                        onChange={(e) => setNewAccessForm((p) => ({ ...p, label: e.target.value }))}
                                    />
                                    <TextField
                                        label="URL" size="small" fullWidth
                                        value={newAccessForm.url}
                                        onChange={(e) => setNewAccessForm((p) => ({ ...p, url: e.target.value }))}
                                    />
                                    <Stack direction="row" spacing={1}>
                                        <TextField
                                            label="Identifiant" size="small" fullWidth
                                            value={newAccessForm.login}
                                            onChange={(e) => setNewAccessForm((p) => ({ ...p, login: e.target.value }))}
                                        />
                                        <TextField
                                            label="Mot de passe" size="small" fullWidth
                                            value={newAccessForm.password}
                                            onChange={(e) => setNewAccessForm((p) => ({ ...p, password: e.target.value }))}
                                        />
                                    </Stack>
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        startIcon={addingAccess ? <CircularProgress size={16} /> : <AddIcon />}
                                        disabled={addingAccess || !newAccessForm.label.trim()}
                                        onClick={handleAddAccess}
                                    >
                                        Ajouter cet accès
                                    </Button>
                                </Stack>
                            </>
                        )}
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeDialog} disabled={saving || uploadingImage}>
                        {editingSite ? 'Fermer' : 'Annuler'}
                    </Button>
                    <Button variant="contained" onClick={handleSave} disabled={saving}>
                        {saving ? <CircularProgress size={20} /> : 'Enregistrer'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Confirmation suppression */}
            <Dialog open={!!deleteTarget} onClose={() => !deleting && setDeleteTarget(null)}>
                <DialogTitle>Supprimer ce site de démo ?</DialogTitle>
                <DialogContent>
                    <Typography>
                        « {deleteTarget?.name} » et ses accès seront définitivement supprimés. Cette action est irréversible.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteTarget(null)} disabled={deleting}>Annuler</Button>
                    <Button color="error" variant="contained" onClick={confirmDelete} disabled={deleting}>
                        {deleting ? <CircularProgress size={20} /> : 'Supprimer'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
